import FirestoreUser, { StudyData, StudyStatus } from '../types/FirestoreUser'

/**
 * Normalizes a study entry to StudyData format.
 * Handles both legacy string format and modern StudyData object format.
 *
 * @param study - A study entry that may be either a string (legacy) or StudyData object
 * @returns A normalized StudyData object with name and status
 *
 * @example
 * normalizeStudy('NCS Dispenza 2025') // { name: 'NCS Dispenza 2025', status: 'Participant' }
 * normalizeStudy({ name: 'Study A', status: 'Active' }) // { name: 'Study A', status: 'Active' }
 */
export function normalizeStudy(study: string | StudyData): StudyData {
  if (typeof study === 'string') {
    return { name: study, status: 'Participant' }
  }
  return study
}

/**
 * Checks if a user is a member of a specific study by study name.
 * Handles both string and StudyData object formats in the user's studies array.
 *
 * @param user - The FirestoreUser to check
 * @param studyName - The name of the study to look for
 * @returns True if the user is a member of the specified study
 *
 * @example
 * userHasStudy(user, 'NCS Dispenza 2025') // true if user is in that study
 */
export function userHasStudy(user: FirestoreUser, studyName: string): boolean {
  if (!user.studies || user.studies.length === 0) return false

  return user.studies.some((study) => {
    const normalized = normalizeStudy(study)
    return normalized.name === studyName
  })
}

/**
 * Extracts all study names from a user's studies array.
 * Handles both string and StudyData object formats.
 *
 * @param user - The FirestoreUser to extract study names from
 * @returns An array of study names
 *
 * @example
 * getUserStudyNames(user) // ['NCS Dispenza 2025', 'Study B']
 */
export function getUserStudyNames(user: FirestoreUser): string[] {
  if (!user.studies || user.studies.length === 0) return []

  return user.studies.map((study) => {
    const normalized = normalizeStudy(study)
    return normalized.name
  })
}

/**
 * Normalizes all entries in a user's studies array to StudyData format.
 * Converts legacy string entries to StudyData objects with default 'Participant' status.
 *
 * @param studies - The studies array from a user document (may be undefined or mixed format)
 * @returns An array of normalized StudyData objects
 *
 * @example
 * normalizeUserStudies(['Study A', { name: 'Study B', status: 'Active' }])
 * // [
 * //   { name: 'Study A', status: 'Participant' },
 * //   { name: 'Study B', status: 'Active' }
 * // ]
 */
export function normalizeUserStudies(
  studies: (string | StudyData)[] | undefined
): StudyData[] {
  if (!studies || !Array.isArray(studies)) return []
  return studies.map(normalizeStudy)
}

/**
 * Returns the Chakra UI colorScheme for a given study status.
 *
 * @param status - The study status value
 * @returns A Chakra UI colorScheme string
 *
 * @example
 * getStudyStatusColorScheme('Applicant')   // 'yellow'
 * getStudyStatusColorScheme('Participant')  // 'green'
 * getStudyStatusColorScheme('Withdrawn')    // 'purple'
 * getStudyStatusColorScheme('Completed')    // 'blue'
 */
export function getStudyStatusColorScheme(status: StudyStatus): string {
  switch (status) {
    case 'Applicant':
      return 'yellow'
    case 'Participant':
      return 'green'
    case 'Withdrawn':
      return 'purple'
    case 'Completed':
      return 'blue'
    default:
      return 'green'
  }
}
